import React from 'react';
import { motion } from 'framer-motion';
import IndustryPageHeading from '../components/IndustryPageHeading';
import EnterpriseVideo from '../components/EnterpriseVideo';

/**
 * Manufacturing Solutions — internal route /solutions/industries/manufacturing
 *
 * Three content sections with alternating layout direction (text/video),
 * mirroring the first three sections of the Logistics Solutions page and
 * matching the Insurance / Real Estate / Retail / Healthcare / Finance
 * industry subpages. Each video slot is an empty champagne-gold-bordered
 * placeholder frame labeled "Video Demo Coming Soon", alternating sides
 * (video right, then left, then right). After the three sections the
 * page goes directly to the site's standard footer, which is rendered
 * globally by Layout.
 */

const VideoDemoFrame = ({ cloudflareVideoId }) => {
  if (!cloudflareVideoId) {
    return (
      <div className="w-full">
        <div
          className="relative w-full overflow-hidden rounded-lg bg-gradient-to-br from-[#E8E4DC] to-[#E8EDE7]"
          style={{
            aspectRatio: '16 / 9',
            border: '2px solid #D4AF37',
            boxShadow:
              'inset 0 2px 8px rgba(20, 30, 48, 0.15), 0 4px 12px rgba(0, 0, 0, 0.1)',
          }}
          data-testid="manufacturing-video-placeholder"
        >
          <div className="absolute inset-0 flex items-center justify-center">
            <p className="text-base sm:text-lg text-black/60 font-medium tracking-wide">
              Video Demo Coming Soon
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      <EnterpriseVideo
        cloudflareVideoId={cloudflareVideoId}
        title="Manufacturing demo video"
        testId={`manufacturing-video-${cloudflareVideoId}`}
        className="w-full rounded-lg bg-gradient-to-br from-[#E8E4DC] to-[#E8EDE7]"
        style={{
          aspectRatio: '16 / 9',
          border: '2px solid #D4AF37',
          boxShadow:
            'inset 0 2px 8px rgba(20, 30, 48, 0.15), 0 4px 12px rgba(0, 0, 0, 0.1)',
        }}
      />
    </div>
  );
};

const FeatureBlock = ({ title, description }) => (
  <div className="pl-4 relative">
    <div
      className="absolute left-0 top-1 bottom-1 w-0.5 rounded-full"
      style={{
        background: 'linear-gradient(to bottom, #D4AF37, rgba(212, 175, 55, 0.4))',
      }}
    />
    <h3 className="text-base sm:text-lg font-medium text-black/90">{title}</h3>
    <p className="text-sm text-black/70 mt-1">{description}</p>
  </div>
);

const TextContent = ({ eyebrow, heading, body, features, isPageHeading }) => {
  const HeadingTag = isPageHeading ? 'h1' : 'h2';
  return (
    <div>
      <p className="text-sm md:text-base font-bold text-black/90 mb-3 md:mb-4 uppercase tracking-wider">
        {eyebrow}
      </p>
      <HeadingTag className="text-3xl md:text-4xl font-medium text-black/90 mb-4 md:mb-6">
        {heading}
      </HeadingTag>
      <p className="text-base sm:text-lg text-black/70 mb-8 md:mb-10">{body}</p>
      <div className="space-y-6">
        {features.map((f) => (
          <FeatureBlock key={f.title} title={f.title} description={f.description} />
        ))}
      </div>
    </div>
  );
};

const sections = [
  {
    eyebrow: 'Production & Quality Control',
    cloudflareVideoId: '821ec59ac3b708bb23b4bbbb07d8a4f7',
    heading: 'Defects caught before they leave the line.',
    body:
      'GoogLyn monitors production in real time, flags quality issues as they emerge, and predicts equipment failures before they cause downtime.',
    features: [
      {
        title: 'AI-powered quality control',
        description: 'Defects detected automatically, before a shipment goes out.',
      },
      {
        title: 'Predictive equipment monitoring',
        description: 'Maintenance alerts sent before a breakdown happens.',
      },
      {
        title: 'Automated production scheduling',
        description: 'Planning and sequencing adjusted in real time.',
      },
    ],
  },
  {
    eyebrow: 'Supply Chain & Fulfillment',
    cloudflareVideoId: '382fefc4e12921a1036cf039f183c6f3',
    heading: 'Every part, tracked from supplier to shipment.',
    body:
      'GoogLyn tracks inventory, coordinates suppliers, and schedules shipments automatically \u2014 keeping production running without a materials shortage catching anyone off guard.',
    features: [
      {
        title: 'Real-time inventory visibility',
        description: 'Stock levels synced across every facility.',
      },
      {
        title: 'Automated supplier coordination',
        description: 'Orders tracked and reordered without manual follow-up.',
      },
      {
        title: 'Streamlined shipment scheduling',
        description: 'Logistics handoffs coordinated automatically.',
      },
    ],
  },
  {
    eyebrow: 'Safety, Compliance & Reporting',
    cloudflareVideoId: '0f83cb5ba7447e68ddda376fe63d9811',
    heading: 'Every line, always audit-ready.',
    body:
      'Safety compliance monitoring, downtime analytics, and reporting run continuously, so plant managers work from real-time data instead of end-of-shift reconstructions.',
    features: [
      {
        title: 'Safety compliance monitoring',
        description: 'Audit trails stay current automatically.',
      },
      {
        title: 'Real-time production reporting',
        description: 'Output and performance data always at your fingertips.',
      },
      {
        title: 'Downtime analytics',
        description: 'Risk patterns flagged before they cause a stoppage.',
      },
    ],
  },
];

const IndustryManufacturing = () => {
  return (
    <div className="w-full" data-testid="manufacturing-solutions-page">
      <IndustryPageHeading name="Manufacturing" testId="manufacturing-page-heading" />
      {sections.map((section, idx) => {
        const textFirst = idx % 2 === 0; // sections 1,3 → text left (video right); 2 → video left
        const bg = idx % 2 === 0 ? 'bg-white' : 'bg-[#F5F7F5]';
        return (
          <section
            key={section.eyebrow}
            className={`py-16 md:py-24 ${bg}`}
            data-testid={`manufacturing-section-${idx + 1}`}
          >
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center"
              >
                {textFirst ? (
                  <>
                    <TextContent {...section} isPageHeading={idx === 0} />
                    <VideoDemoFrame cloudflareVideoId={section.cloudflareVideoId} />
                  </>
                ) : (
                  <>
                    {/* Video first on desktop; text stays above video on mobile for readability */}
                    <div className="order-2 lg:order-1">
                      <VideoDemoFrame cloudflareVideoId={section.cloudflareVideoId} />
                    </div>
                    <div className="order-1 lg:order-2">
                      <TextContent {...section} isPageHeading={idx === 0} />
                    </div>
                  </>
                )}
              </motion.div>
            </div>
          </section>
        );
      })}
    </div>
  );
};

export default IndustryManufacturing;
